import { useState } from 'react'
import { CheckCircle2, Clock, MapPin, Play, Plus, RefreshCw } from 'lucide-react'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { fmtDate, fmtNumber, fmtRelative } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { TaskResponse } from '@/shared/api/types'
import { acceptTask, completeTask, listTasks, myFieldProfile, startTask } from './api'
import {
  BadgeChip, PriorityChip, RoleBadge, TaskStatusChip, TierChip, missionKindLabel, scoreNumber,
} from './vocab'
import { CaptureDialog } from './CaptureDialog'

/**
 * My day — the screen a mobilizer opens.
 *
 * ## A campaign manager is not an error
 *
 * `GET /mobilizers/me` always returns 200 with a body. Somebody who is not field
 * staff simply has no field identity, and this screen says so and offers the
 * roster instead of showing them a 403 they cannot act on.
 *
 * ## Nothing here is a live score
 *
 * Everything under "How you're doing" comes from the nightly rollup, and the
 * screen prints when it last ran. Users otherwise assume it is live and report
 * the lag as a bug. Before the first run the numbers are **null, not zero** —
 * "Score 0, Bronze, rank 4000" on somebody's first morning is the worst possible
 * introduction to the system.
 *
 * ## Derived work sits in the same list, marked
 *
 * "Twelve voters in your ward have never been verified" is a query, not a row.
 * It appears beside the real tasks because that is where somebody looks for what
 * to do next, and it carries no accept button because there is nothing to accept.
 */
export function MyDayPage() {
  const [capture, setCapture] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const me = useLoad(() => myFieldProfile(), [])
  const d = me.data

  /**
   * `GET /mobilizers/me` is authenticated-only, on purpose. `GET /mobilizers/tasks`
   * is not — it needs `mobilizer:read`, and the seeded MOBILIZER role is only
   * guaranteed `mobilizer:task`. So the richer list is fetched ONLY when the
   * caller may read it, and everybody else gets the summaries `/me` already
   * returned. Fetching it unconditionally would 403 the screen for exactly the
   * people it was built for.
   */
  const canReadTasks = useCan('mobilizer:read')

  const tasks = useLoad(
    () => (canReadTasks && d?.isMobilizer && d.profile
      ? listTasks({ mobilizerId: d.profile.id })
      : Promise.resolve([])),
    [canReadTasks, d?.isMobilizer, d?.profile?.id])

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id); setError(null)
    try {
      await fn()
      await tasks.reload()
      await me.reload()
    } catch (err) { setError(explain(err)) } finally { setBusy(null) }
  }

  if (me.loading && !d) {
    return <Panel><LoadingBlock label="Working out who you are in the field…" /></Panel>
  }

  if (d && !d.isMobilizer) {
    return (
      <div className="space-y-4">
        <SectionHeader title="My day"
                       description="This is the screen field staff open to see their own work." />
        <Panel>
          <EmptyState
            title="You have no field identity"
            hint="Your account is not linked to a mobilizer record, which is normal for office staff — a mobilizer is a person, and most of them have no login at all. A coordinator can link the two from the roster."
          />
        </Panel>
      </div>
    )
  }

  const p = d?.profile
  const stats = d?.stats
  const score = scoreNumber(stats?.score)
  const open = (tasks.data ?? []).filter(
    (t) => !['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(t.statusCode))
  const derived = (d?.tasks ?? []).filter((t) => t.derived)
  // The fallback for a caller without `mobilizer:read`: `/me` already carried
  // their assigned work, without the transition list, so it renders read-only.
  const summaryOnly = !canReadTasks
    ? (d?.tasks ?? []).filter((t) => !t.derived)
    : []

  return (
    <div className="space-y-4">
      <SectionHeader
        title={p ? `Hello, ${p.fullName.split(' ')[0]}` : 'My day'}
        description={d?.scope
          ? [d.scope.countyName, d.scope.constituencyName, d.scope.wardName, d.scope.stationName]
            .filter(Boolean).join(' → ')
          : 'Your scope will appear here once you have a home unit.'}
        right={(
          <div className="flex gap-1.5">
            <button type="button" className="erp-btn" aria-label="Refresh"
                    onClick={() => { void me.reload(); void tasks.reload() }}>
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            <button type="button" className="erp-btn-primary" onClick={() => setCapture(true)}>
              <Plus className="h-3.5 w-3.5" /> Record something
            </button>
          </div>
        )}
      />

      <ErrorStrip message={error ?? me.error ?? tasks.error} />

      {p && (
        <div className="flex flex-wrap items-center gap-2">
          <RoleBadge code={p.roleCode} />
          {stats?.tier && <TierChip code={stats.tier} />}
          {(stats?.badges ?? []).map((b) => <BadgeChip key={b} code={b} />)}
        </div>
      )}

      <KpiStrip>
        <MetricCard label="Today" value={fmtNumber(stats?.activitiesToday ?? 0)}
                    sub="things you recorded" accent />
        <MetricCard label="This week" value={fmtNumber(stats?.activitiesThisWeek ?? 0)}
                    sub="across all types" />
        <MetricCard label="Open tasks" value={fmtNumber(open.length)}
                    sub={derived.length ? `${derived.length} more suggested below` : 'nothing else waiting'} />
        <MetricCard label="Stations you own" value={fmtNumber(stats?.stationsAdopted ?? 0)}
                    sub="adopted, not just assigned" />
        <MetricCard
          label="Last check-in"
          value={stats?.lastCheckInAt ? fmtRelative(stats.lastCheckInAt) : '—'}
          sub={stats?.lastCheckInUnitName ?? 'no check-in yet'}
        />
      </KpiStrip>

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Panel
          title="Your work"
          description="Progress is counted from what you record, not typed in. You can finish a task under target — say why in the note."
        >
          {tasks.loading && !tasks.data ? (
            <LoadingBlock label="Loading your tasks…" />
          ) : open.length === 0 && derived.length === 0 && summaryOnly.length === 0 ? (
            <EmptyState title="Nothing waiting"
                        hint="New tasks appear here when a coordinator assigns a mission." />
          ) : (
            <ul className="divide-y divide-border">
              {open.map((t) => (
                <TaskRow key={t.id} task={t} busy={busy === t.id}
                         onAccept={() => void act(t.id, () => acceptTask(t.id))}
                         onStart={() => void act(t.id, () => startTask(t.id))}
                         onComplete={(note) => void act(t.id, () => completeTask(t.id, note))} />
              ))}
              {summaryOnly.map((t) => (
                <li key={t.id ?? t.title} className="py-3 flex items-start gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-erp-sm font-medium">{t.title}</span>
                    <span className="flex flex-wrap items-center gap-1.5 mt-1">
                      <TaskStatusChip code={t.statusCode} />
                      <PriorityChip code={t.priorityCode} />
                      {t.dueOn && (
                        <span className="text-erp-xs text-muted-foreground">Due {fmtDate(t.dueOn)}</span>
                      )}
                    </span>
                  </span>
                  <span className="erp-metric text-erp-sm">
                    {t.progress}{t.target ? ` / ${t.target}` : ''}
                  </span>
                </li>
              ))}
              {derived.map((t, i) => (
                <li key={`derived-${i}`} className="py-3 flex items-start gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-erp-sm">{t.title}</span>
                    <span className="flex flex-wrap items-center gap-1.5 mt-1">
                      <span className="text-erp-xs text-muted-foreground">
                        {missionKindLabel(t.kindCode)}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 text-erp-xs border rounded-md bg-muted text-muted-foreground border-border">
                        Suggested
                      </span>
                    </span>
                  </span>
                  <span className="erp-metric text-erp-sm">{t.progress}</span>
                </li>
              ))}
            </ul>
          )}
          {derived.length > 0 && (
            <p className="text-erp-xs text-muted-foreground mt-3 pt-3 border-t border-border">
              Suggested items are a live query over your ward, not assigned work. There is
              nothing to accept — do them and the count goes down on its own.
            </p>
          )}
        </Panel>

        <Panel
          title="How you're doing"
          description={stats?.computedAt
            ? `Worked out ${fmtRelative(stats.computedAt)}, overnight.`
            : 'Nothing computed yet.'}
        >
          {score === null ? (
            <Note tone="info">
              Your score has not been worked out yet. It is computed overnight, so the
              first one appears tomorrow morning — not zero today.
            </Note>
          ) : (
            <dl className="space-y-2">
              <Stat label="Score" value={score.toLocaleString('en-KE')} />
              <Stat label="Tier" value={<TierChip code={stats?.tier} />} />
              <Stat label="Rank in your unit"
                    value={stats?.rank ? `#${stats.rank}` : '—'} />
              {/* 0 is a real answer — "you broke your streak" — and only null
                  means "not worked out yet". */}
              <Stat label="Streak"
                    value={stats?.streakDays == null ? '—' : `${stats.streakDays} days`} />
              <Stat label="Voters recorded" value={fmtNumber(stats?.votersRecorded ?? 0)} />
              <Stat label="Transfers raised" value={fmtNumber(stats?.transfersRaised ?? 0)} />
              {(stats?.flaggedCount ?? 0) > 0 && (
                <div className="pt-2 border-t border-border">
                  <Note tone="bad">
                    {stats?.flaggedCount} of your activities were flagged by the quality
                    check and are not counted in your score. They are still in the feed —
                    nothing was deleted. Ask your coordinator if that looks wrong.
                  </Note>
                </div>
              )}
            </dl>
          )}
        </Panel>
      </div>

      {p && (
        <CaptureDialog
          open={capture}
          defaultUnitId={p.primaryUnitId ?? null}
          defaultUnitName={p.primaryUnitName ?? null}
          onClose={() => setCapture(false)}
          // Deliberately does NOT close the dialog. Closing on success unmounts
          // the panel that renders the confirmation, so the user sees the modal
          // vanish and has to guess whether anything happened. The dialog says
          // what it did and offers "Done".
          onRecorded={() => { void me.reload() }}
        />
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-erp-xs text-muted-foreground">{label}</dt>
      <dd className="text-erp-sm erp-metric">{value}</dd>
    </div>
  )
}

/**
 * The buttons come from `allowedTransitions`, not from a copy of the state
 * machine kept here. A second copy is one release away from offering a button
 * the server refuses — which is exactly the "this build does not know how to
 * perform X" failure the transfer screen shipped with.
 */
function TaskRow({
  task, busy, onAccept, onStart, onComplete,
}: {
  task: TaskResponse
  busy: boolean
  onAccept: () => void
  onStart: () => void
  onComplete: (note?: string) => void
}) {
  const [noting, setNoting] = useState(false)
  const [note, setNote] = useState('')
  const can = (s: string) => task.allowedTransitions.includes(s as never)
  const live = task.liveProgressCount ?? task.progressCount

  return (
    <li className="py-3">
      <div className="flex items-start gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-erp-sm font-medium truncate">
            {task.missionTitle ?? task.missionCode}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 mt-1">
            <TaskStatusChip code={task.statusCode} />
            <PriorityChip code={task.priorityCode} />
            {task.unitName && (
              <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
                <MapPin className="h-3 w-3" /> {task.unitName}
              </span>
            )}
            {task.dueOn && (
              <span className="inline-flex items-center gap-1 text-erp-xs text-muted-foreground">
                <Clock className="h-3 w-3" /> due {fmtDate(task.dueOn)}
              </span>
            )}
          </span>
        </span>
        <span className="text-right shrink-0">
          <span className="block erp-metric text-erp-sm">
            {live}{task.targetCount ? ` / ${task.targetCount}` : ''}
          </span>
          {task.liveProgressCount !== null && task.liveProgressCount !== undefined
            && task.liveProgressCount !== task.progressCount && (
            <span className="block text-erp-xs text-muted-foreground">
              {task.progressCount} counted overnight
            </span>
          )}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 mt-2">
        {can('ACCEPTED') && (
          <button type="button" className="erp-btn" disabled={busy} onClick={onAccept}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Accept
          </button>
        )}
        {can('IN_PROGRESS') && (
          <button type="button" className="erp-btn" disabled={busy} onClick={onStart}>
            <Play className="h-3.5 w-3.5" /> Start
          </button>
        )}
        {can('COMPLETED') && !noting && (
          <button type="button" className="erp-btn" disabled={busy} onClick={() => setNoting(true)}>
            Finish
          </button>
        )}
      </div>

      {noting && (
        <div className="mt-2 space-y-2">
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Anything worth recording? Finishing under target is fine — say why.
            </span>
            <input className="erp-input w-full" value={note}
                   onChange={(e) => setNote(e.target.value)}
                   placeholder="The rest have moved away" />
          </label>
          <div className="flex gap-1.5">
            <button type="button" className="erp-btn" onClick={() => { setNoting(false); setNote('') }}>
              Cancel
            </button>
            <button type="button" className="erp-btn-primary" disabled={busy}
                    onClick={() => { onComplete(note.trim() || undefined); setNoting(false); setNote('') }}>
              Mark finished
            </button>
          </div>
        </div>
      )}
    </li>
  )
}
